/** Delivery statuses — one vocabulary for every console surface that shows one: the
 *  delivery-logs grid, the delivery-log detail, a booking's delivery logs and its time axis.
 *
 *  Codes mirror the backend: `Event_Delivery_Log.STATUSES` per event (the legacy
 *  `Event_Delivery_Attempt` shares the numbering) and `Event_Delivery_Notification_Log.STATUSES`
 *  per delivery method. Event 13–15 and method 6–8 are a **sandbox tenant's** (SBX plan D5,
 *  dev-platform S4a1/S4b): sent to allowlisted destinations only, partly sent and partly captured,
 *  or captured in the sandbox's outbox. They are expected outcomes — never errors, never alerted —
 *  so every surface shows them neutral, never as a success or a failure. A production tenant never
 *  has them. */

/** The sandbox statuses' words; the same three on an event, a method and a message row. */
export const SANDBOX_STATUS_LABEL = {
  sentAllowlisted: 'Sent (allowlisted)',
  partiallySentAllowlisted: 'Partially sent (allowlisted)',
  captured: 'Captured (sandbox)',
} as const;

/** `Event_Delivery_Log.STATUSES` codes. */
export const EVENT_STATUS = {
  EMITTED: 0,
  NO_MATCH: 1,
  RENDERING: 2,
  SCHEDULED: 3,
  SENT: 4,
  ERROR: 5,
  PARTIALLY_SENT: 6,
  NOT_SENT: 7,
  SENT_WITH_ERRORS: 8,
  /** Transient — the recovery cron moves every orphan to one of the three below. */
  ORPHANED: 9,
  ORPHANED_RECOVERED: 10,
  ORPHANED_UNRECOVERABLE: 11,
  /** Examined, nothing was left to deliver — not a problem. */
  ORPHANED_EMPTY: 12,
  SENT_ALLOWLISTED: 13,
  PARTIALLY_SENT_ALLOWLISTED: 14,
  CAPTURED: 15,
} as const;

/** `Event_Delivery_Notification_Log.STATUSES` codes (one delivery method of one notification). */
export const METHOD_STATUS = {
  INITIATED: 0,
  SENT: 1,
  PARTIALLY_SENT: 2,
  NOT_SENT: 3,
  ERROR: 4,
  REJECTED_BY_POLICY: 5,
  SENT_ALLOWLISTED: 6,
  PARTIALLY_SENT_ALLOWLISTED: 7,
  CAPTURED: 8,
} as const;

const SANDBOX_EVENT_STATUSES = new Map<number, string>([
  [EVENT_STATUS.SENT_ALLOWLISTED, SANDBOX_STATUS_LABEL.sentAllowlisted],
  [EVENT_STATUS.PARTIALLY_SENT_ALLOWLISTED, SANDBOX_STATUS_LABEL.partiallySentAllowlisted],
  [EVENT_STATUS.CAPTURED, SANDBOX_STATUS_LABEL.captured],
]);

const SANDBOX_METHOD_STATUSES = new Map<number, string>([
  [METHOD_STATUS.SENT_ALLOWLISTED, SANDBOX_STATUS_LABEL.sentAllowlisted],
  [METHOD_STATUS.PARTIALLY_SENT_ALLOWLISTED, SANDBOX_STATUS_LABEL.partiallySentAllowlisted],
  [METHOD_STATUS.CAPTURED, SANDBOX_STATUS_LABEL.captured],
]);

/** A sandbox's event status (13–15). */
export function isSandboxEventStatus(status: number | null | undefined): boolean {
  return status != null && SANDBOX_EVENT_STATUSES.has(status);
}

/** A sandbox's method status (6–8): its words, or null for any other status. */
export function sandboxMethodLabel(status: number | null | undefined): string | null {
  return status == null ? null : (SANDBOX_METHOD_STATUSES.get(status) ?? null);
}

/** How a status chip reads. `neutral` is the outlined grey chip: in-flight states, the
 *  "nothing to do" ones, and every sandbox outcome. */
export type StatusTone = 'success' | 'error' | 'warning' | 'neutral';

/** The tone of an event's status. */
export function eventStatusTone(status: number): StatusTone {
  switch (status) {
    case EVENT_STATUS.SENT:
    case EVENT_STATUS.ORPHANED_RECOVERED:
      return 'success';
    case EVENT_STATUS.ERROR:
    case EVENT_STATUS.SENT_WITH_ERRORS:
    case EVENT_STATUS.ORPHANED_UNRECOVERABLE:
      return 'error';
    case EVENT_STATUS.PARTIALLY_SENT:
    case EVENT_STATUS.NOT_SENT:
    case EVENT_STATUS.ORPHANED:
      return 'warning';
    default:
      // Emitted … Scheduled, Orphaned and empty, and the sandbox outcomes 13–15.
      return 'neutral';
  }
}

/** The tone of a delivery method's status. */
export function methodStatusTone(status: number): StatusTone {
  if (status === METHOD_STATUS.SENT) return 'success';
  if (status === METHOD_STATUS.ERROR) return 'error';
  // Initiated, partially / not sent, rejected by policy, and the sandbox outcomes 6–8.
  return 'neutral';
}

/** The captured part of a counts line ("3 captured"), or null when nothing was captured —
 *  every production row, so production counts read exactly as before. */
export function capturedCount(n: number | null | undefined): string | null {
  return n ? `${n} captured` : null;
}
